import { FC } from 'react';
import type { RemediationReviewItem } from '@libs/types';
import { Box, IconButton, type Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useDirection } from '@hooks';
import { RemediationDecision, RemediationItemStatus, RemediationItemType } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import { CaretLeftIcon, CaretRightIcon, FilesIcon, SpinnerIcon } from '@phosphor-icons/react';

import { hasContentLanded } from '../utils/run-progress';

export const DOC_RAIL_WIDTH = 240;
export const DOC_RAIL_COLLAPSED_WIDTH = 44;

/**
 * Every document in the run, as a list you can see at once.
 *
 * This replaced a `<select>` in the page title. A dropdown answers "which
 * document am I on" and nothing else: to find the one still needing decisions
 * you had to open it, read five rows, and close it again — and it hid the two
 * facts that decide where to go next, which are how much is left on each
 * document and which ones failed. Those are now on the row.
 *
 * Deliberately thinner than the design it comes from. That version gave each
 * row a full status pill and let the name wrap to three lines, so five
 * documents filled the column and the list stopped being scannable. A name on
 * one line, one line of counts underneath, and a coloured dot carrying the
 * status say the same things in a third of the height.
 */
export const ReviewDocumentRail: FC<{
  items: RemediationReviewItem[];
  activeId: string | null;
  onSelect: (itemId: string) => void;
  open: boolean;
  onToggle: () => void;
}> = observer(({ items, activeId, onSelect, open, onToggle }) => {
  const theme = useTheme();
  const { t } = useTrans();
  const { isRtl } = useDirection();

  if (!open) {
    return (
      <Box
        sx={{
          width: DOC_RAIL_COLLAPSED_WIDTH,
          flexShrink: 0,
          borderInlineEnd: `1px solid ${theme.palette.grey[300]}`,
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          pt: 1,
        }}
      >
        <Tooltip title={t('app.remediation.review.documents')}>
          <IconButton size="small" onClick={onToggle}>
            {/* Points the way it will move. MUI mirrors nothing here, so the
                icon is picked from the writing direction rather than fixed. */}
            {isRtl ? <CaretLeftIcon size={16} /> : <CaretRightIcon size={16} />}
          </IconButton>
        </Tooltip>
        <FilesIcon size={15} color={theme.palette.text.tertiary} />
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.tertiary' }}>
          {items.length}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: DOC_RAIL_WIDTH,
        flexShrink: 0,
        borderInlineEnd: `1px solid ${theme.palette.grey[300]}`,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: 'text.tertiary',
          }}
        >
          {t('app.remediation.review.documents')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.tertiary' }}>
            {items.length}
          </Typography>
          <IconButton size="small" onClick={onToggle} sx={{ p: 0.25 }}>
            {isRtl ? <CaretRightIcon size={14} /> : <CaretLeftIcon size={14} />}
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
        {items.map((item) => (
          <DocumentRow
            key={item.itemId}
            item={item}
            active={item.itemId === activeId}
            onSelect={() => onSelect(item.itemId)}
          />
        ))}
      </Box>
    </Box>
  );
});

/**
 * The status colours are the same ones the run screen's rows use, so a document
 * that was amber there is amber here. Only the shape changes — a dot rather than
 * a pill, because in a 240px column the pill was the widest thing on the row and
 * pushed the name it was describing onto a second line.
 */
function dotColour(theme: Theme, status: RemediationItemStatus): string {
  switch (status) {
    case RemediationItemStatus.FAILED:
      return theme.palette.error.main;
    case RemediationItemStatus.ACCEPTED:
      return theme.palette.green[700];
    case RemediationItemStatus.REVIEW_PENDING:
      return theme.palette.warning.main;
    default:
      // REJECTED and NO_CHANGES: both settled, neither an error. Grey says
      // "nothing left to do here" without claiming something went wrong.
      return theme.palette.text.tertiary;
  }
}

const DocumentRow: FC<{
  item: RemediationReviewItem;
  active: boolean;
  onSelect: () => void;
}> = observer(({ item, active, onSelect }) => {
  const theme = useTheme();
  const { t } = useTrans();

  const name = item.documentName ?? t('app.remediation.review.title');
  // Nothing has been written yet, so there is nothing to open. Shown rather
  // than hidden: the reader should be able to see the whole run.
  const stillWriting = !hasContentLanded(item.status);

  // One line, chosen for what it tells a reviewer deciding where to go next.
  // A document with changes gets its progress; the others get the reason they
  // have none, which is what stops "0 of 0 decided" appearing on a document
  // that was never going to have any.
  const changeSet = item.changeSet;
  const changes = changeSet && changeSet.mode !== 'WHOLE_DOCUMENT' ? changeSet.changes : [];
  const decided = changes.filter((c) => c.decision !== RemediationDecision.PENDING).length;

  const meta = stillWriting
    ? t('app.remediation.review.stillWriting')
    : item.status === RemediationItemStatus.FAILED
      ? t('app.remediation.itemStatus.failed')
      : item.type === RemediationItemType.GENERATE
        ? t('app.remediation.itemType.GENERATE')
        : changes.length === 0
          ? t('app.remediation.review.noChangesTitle')
          : t('app.remediation.review.decidedCount', { decided, total: changes.length });

  return (
    <Box
      component="button"
      type="button"
      disabled={stillWriting}
      onClick={onSelect}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        width: '100%',
        px: 1.5,
        py: 1.125,
        textAlign: 'start',
        cursor: 'pointer',
        border: 'none',
        fontFamily: 'inherit',
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        // The marker is a border rather than a background stripe so the row's
        // own tint can stay flat — two overlapping fills read as a gradient.
        borderInlineStart: `3px solid ${active ? theme.palette.ocean[700] : 'transparent'}`,
        backgroundColor: active ? theme.palette.ocean[100] : 'transparent',
        '&:hover': { backgroundColor: active ? theme.palette.ocean[100] : theme.palette.grey[100] },
        '&:disabled': { cursor: 'default', opacity: 0.7 },
      }}
    >
      {/* A spinner where the status dot goes, so the row reads as "being
          worked on" at the same glance the others read as their outcome. */}
      {stillWriting ? (
        <Box sx={{ mt: 0.25, flexShrink: 0, display: 'flex' }}>
          <SpinnerIcon size={12} color={theme.palette.ocean[700]} />
        </Box>
      ) : (
        <Box
          sx={{
            width: 7,
            height: 7,
            mt: 0.625,
            borderRadius: '50%',
            flexShrink: 0,
            backgroundColor: dotColour(theme, item.status),
          }}
        />
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="textSM_regular"
          noWrap
          title={name}
          sx={{ display: 'block', fontWeight: active ? 600 : 500 }}
        >
          {name}
        </Typography>
        <Typography
          variant="textXS_regular"
          noWrap
          sx={{ display: 'block', color: 'text.tertiary', mt: 0.125 }}
        >
          {meta}
        </Typography>
      </Box>
    </Box>
  );
});
